import {
  createAsyncThunk,
  type ThunkDispatch,
  type Action,
} from '@reduxjs/toolkit';

import { updateCalleeConnectionString } from 'features/call/call-base/store/callSlice';
import type { RootState } from 'features/app/store/store';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { deafPhoneNumberSelector } from 'features/call/call-deaf/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { handleError } from 'features/notification/store';
import { CallDaoService } from 'features/call/call-base/services';
import {
  addPlusToPhoneNumber,
  parsePhoneNumber,
  normalizePhoneNumber,
  doPhoneNumbersDifferOnlyBy011,
  isNANPNumber,
} from 'features/call/call-base/helpers';

/**
 * Check and update callee as needed
 */
export const checkAndUpdateCallee = createAsyncThunk(
  'call/checkAndUpdateCallee',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const isDeafToHearing = state.call.isDeafToHearing;
    const calleeConnectionString = state.call.calleeConnectionString;
    const callId = callIdSelector(state);

    // Case 1: Missing calleeConnectionString - needs update
    if (!calleeConnectionString) {
      if (isDeafToHearing) {
        await updateMissingDeafToHearingCallee(dispatch, state, callId);
      } else {
        await updateMissingHearingToDeafCallee(dispatch, state, callId);
      }
      return;
    }

    // Case 2: Check if the existing calleeConnectionString matches the expected value
    if (isDeafToHearing) {
      await updateDeafToHearingCallee(
        dispatch,
        state,
        callId,
        calleeConnectionString
      );
    } else {
      await updateHearingToDeafCallee(
        dispatch,
        state,
        callId,
        calleeConnectionString
      );
    }
  }
);

/**
 * Helper function for missing deaf-to-hearing callee
 */
export const updateMissingDeafToHearingCallee = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  state: RootState,
  callId: number
): Promise<void> => {
  const primaryHearingPhoneNumber = primaryHearingPhoneNumberSelector(state);
  if (!primaryHearingPhoneNumber) {
    return;
  }

  dispatch(
    sendAnalyticsInfo({
      Method: 'checkAndUpdateCallee',
      Message: `Missing calleeConnectionString, updating to ${primaryHearingPhoneNumber}`,
    })
  );
  await updateCalleePhoneNumberTo(dispatch, callId, primaryHearingPhoneNumber);
};

/**
 * Updates the callee phone number
 */
export const updateCalleePhoneNumberTo = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  callId: number,
  phoneNumber: string
): Promise<void> => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber);
    const phoneNumberWithPlus = addPlusToPhoneNumber(parsedNumber);

    const call = await CallDaoService.updateCalleePhoneNumber({
      callId,
      phoneNumber: phoneNumberWithPlus,
    });

    dispatch(updateCalleeConnectionString(call.CalleeConnectionString));
  } catch (error) {
    dispatch(
      handleError({
        error,
        methodName: 'checkAndUpdateCallee',
        message: 'Failed to update callee phone number',
      })
    );
  }
};

/**
 * Helper function for missing hearing-to-deaf callee
 */
export const updateMissingHearingToDeafCallee = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  state: RootState,
  callId: number
): Promise<void> => {
  const deafPhoneNumber = deafPhoneNumberSelector(state);
  if (!deafPhoneNumber) {
    return;
  }

  dispatch(
    sendAnalyticsInfo({
      Method: 'checkAndUpdateCallee',
      Message: `Missing calleeConnectionString, updating to ${deafPhoneNumber}`,
    })
  );
  await updateCalleePhoneNumberTo(dispatch, callId, deafPhoneNumber);
};

/**
 * Helper function to update deaf-to-hearing callee
 */
export const updateDeafToHearingCallee = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  state: RootState,
  callId: number,
  calleeConnectionString: string
): Promise<void> => {
  const primaryHearingPhoneNumber = primaryHearingPhoneNumberSelector(state);
  if (!primaryHearingPhoneNumber) {
    return;
  }

  if (!doPhoneNumbersMatch(primaryHearingPhoneNumber, calleeConnectionString)) {
    await handleCalleeUpdateWhenNumbersDontMatch(
      dispatch,
      callId,
      primaryHearingPhoneNumber,
      calleeConnectionString,
      'Primary hearing'
    );
  }
};

/**
 * Helper function to update hearing-to-deaf callee
 */
export const updateHearingToDeafCallee = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  state: RootState,
  callId: number,
  calleeConnectionString: string
): Promise<void> => {
  const deafPhoneNumber = deafPhoneNumberSelector(state);
  if (!deafPhoneNumber) {
    return;
  }

  if (!doPhoneNumbersMatch(deafPhoneNumber, calleeConnectionString)) {
    await handleCalleeUpdateWhenNumbersDontMatch(
      dispatch,
      callId,
      deafPhoneNumber,
      calleeConnectionString,
      'Deaf'
    );
  }
};

/**
 * Update callee phone number
 */
export const updateCalleePhoneNumber = createAsyncThunk(
  'call/updateCalleePhoneNumber',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const primaryHearingPhoneNumber = primaryHearingPhoneNumberSelector(state);

    dispatch(
      sendAnalyticsInfo({
        Method: 'updateCalleePhoneNumber',
        Message: `Updating callee phone number to ${primaryHearingPhoneNumber}`,
      })
    );

    try {
      const call = await CallDaoService.updateCalleePhoneNumber({
        callId,
        phoneNumber: addPlusToPhoneNumber(
          parsePhoneNumber(primaryHearingPhoneNumber)
        ),
      });

      dispatch(updateCalleeConnectionString(call.CalleeConnectionString));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'updateCalleePhoneNumber',
        })
      );
    }
  }
);

/**
 * Checks if two phone numbers match after normalization
 */
export const doPhoneNumbersMatch = (
  phoneNumber1: string,
  phoneNumber2: string
): boolean => {
  const normalized1 = normalizePhoneNumber(phoneNumber1);
  const normalized2 = normalizePhoneNumber(phoneNumber2);
  return normalized1 === normalized2;
};

/**
 * Logs analytics for phone number mismatch
 */
export const logPhoneNumberMismatch = (
  dispatch: ThunkDispatch<any, unknown, Action>,
  phoneNumber: string,
  calleeConnectionString: string,
  callDirection: string
): void => {
  dispatch(
    sendAnalyticsInfo({
      Method: 'checkAndUpdateCallee',
      Message: `${callDirection} phone number ${phoneNumber} doesn't match callee connection string ${calleeConnectionString}, updating`,
    })
  );
};

/**
 * Checks if the calleeConnectionString should be converted from 011 format to NANP format
 */
export const shouldConvert011ToNANP = (
  calleeConnectionString: string
): boolean => {
  if (!calleeConnectionString) {
    return false;
  }

  // Check if it starts with 011
  const digits = calleeConnectionString.replaceAll(/\D/g, '');
  if (!digits.startsWith('011')) {
    return false;
  }

  // Remove 011 prefix and check if it's a NANP number
  const numberWithout011 = digits.substring(3);
  return isNANPNumber(numberWithout011);
};

/**
 * Helper function to handle callee update logic when phone numbers don't match
 */
export const handleCalleeUpdateWhenNumbersDontMatch = async (
  dispatch: ThunkDispatch<any, unknown, Action>,
  callId: number,
  expectedPhoneNumber: string,
  calleeConnectionString: string,
  callDirection: string
): Promise<void> => {
  // Check if calleeConnectionString has 011 and is a NANP number that should be converted
  if (shouldConvert011ToNANP(calleeConnectionString)) {
    dispatch(
      sendAnalyticsInfo({
        Method: 'checkAndUpdateCallee',
        Message: `Callee connection string ${calleeConnectionString} has 011 prefix for NANP number, proceeding with update to proper format`,
      })
    );
    // Don't return early - let the existing logic handle the update
  }

  // Skip update if the only difference is the 011 prefix (but not for NANP numbers)
  if (
    doPhoneNumbersDifferOnlyBy011(
      expectedPhoneNumber,
      calleeConnectionString
    ) &&
    !shouldConvert011ToNANP(calleeConnectionString)
  ) {
    dispatch(
      sendAnalyticsInfo({
        Method: 'checkAndUpdateCallee',
        Message: `${callDirection} phone number ${expectedPhoneNumber} and callee connection string ${calleeConnectionString} differ only by 011 prefix, skipping update`,
      })
    );
    return;
  }

  if (!shouldConvert011ToNANP(calleeConnectionString)) {
    logPhoneNumberMismatch(
      dispatch,
      expectedPhoneNumber,
      calleeConnectionString,
      callDirection
    );
  }

  await updateCalleePhoneNumberTo(dispatch, callId, expectedPhoneNumber);
};
